import React, { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import CustomQuotePage from "./pages/CustomQuotePage";

// Lazy load admin pages
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminInquiries = lazy(() => import("./pages/admin/AdminInquiries"));
const AdminQuotes = lazy(() => import("./pages/admin/AdminQuotes"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminRewards = lazy(() => import("./pages/admin/AdminRewards"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));

// Quote resume page
const QuoteResume = lazy(() => import("./pages/QuoteResume"));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-[var(--brand-green)] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Layout wrapper that conditionally shows header/footer
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  return (
    <>
      {!isAdminRoute && <Header />}
      <main className="min-h-screen">
        {children}
      </main>
      {!isAdminRoute && <Footer />}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Admin routes - no header/footer */}
          <Route path="/admin/login" element={
            <Suspense fallback={<PageLoader />}>
              <AdminLogin />
            </Suspense>
          } />
          <Route path="/admin/*" element={
            <Suspense fallback={<PageLoader />}>
              <AdminLayout>
                <Routes>
                  <Route index element={<AdminDashboard />} />
                  <Route path="inquiries" element={<AdminInquiries />} />
                  <Route path="quotes" element={<AdminQuotes />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="rewards" element={<AdminRewards />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="audit-logs" element={<AdminAuditLogs />} />
                </Routes>
              </AdminLayout>
            </Suspense>
          } />
          
          {/* Public routes - with header/footer */}
          <Route path="/*" element={
            <AppLayout>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/shop/:token" element={<ShopPage />} />
                <Route path="/quote/:token" element={
                  <Suspense fallback={<PageLoader />}>
                    <QuoteResume />
                  </Suspense>
                } />
                <Route path="/custom-quote" element={<CustomQuotePage />} />
              </Routes>
            </AppLayout>
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
